// Case study content. Outcomes/numbers are illustrative directional figures
// based on the engagement profile — edit freely with client-approved metrics.

export const CASE_STUDIES = [
  {
    slug: "girmantech",
    client: "Girman Tech",
    accent: "cyan",
    industry: "IT Services",
    url: "https://girmantech.com",
    duration: "Multi-year engagement",
    services: [
      "Legacy Modernization",
      "Application Development",
      "DevOps",
      "Cloud",
      "Contract Engineers",
    ],
    summary:
      "A long-running, multi-workstream partnership where United Techlab serves as Girman Tech's accountable engineering bench, modernizing legacy systems, shipping greenfield web & mobile applications, and embedding senior engineers directly into client-facing delivery teams.",
    headline:
      "From a single project to an embedded engineering bench across modernization, app dev, and cloud.",
    pullQuote:
      "United Techlab is the engineering muscle behind some of our most demanding client deliveries. They ship like an in-house team, but with the rigor of an independent verification partner.",
    pullQuoteRole: "Engineering Leadership, Girman Tech",
    problem: [
      "Multiple downstream clients with overlapping needs across legacy modernization, web/mobile development, DevOps, and cloud, but limited senior bandwidth to deliver them concurrently.",
      "Pressure to keep delivery quality consistent across very different stacks and verticals without compromising velocity.",
      "A need for engineering partners who could move from architect-on-call to hands-on contract engineer without a context switch.",
    ],
    approach: [
      "Set up a verification-first delivery cadence, with every milestone shipped with executable behavioral baselines so regressions surface before UAT.",
      "Embedded United Techlab engineers directly into Girman's client-facing teams as long-running contract engineers across multiple programs.",
      "Stood up shared cloud + DevOps tooling (CI/CD, IaC, observability) so each new project inherited a battle-tested baseline instead of starting from zero.",
      "Brought in modern AI tooling where it accelerated delivery: codegen, summarization, retrieval, without compromising auditability.",
    ],
    outcomes: [
      { label: "Concurrent client programs supported", value: "8+" },
      { label: "Senior engineers embedded", value: "12+" },
      { label: "Faster setup on new programs", value: "~60%" },
      { label: "Production incidents post-cutover", value: "0 critical" },
    ],
    techUsed: [
      "AWS",
      "Google Cloud",
      "Kubernetes",
      "Docker",
      "Terraform",
      "Python",
      "Node.js",
      "React",
      "React Native",
      "TypeScript",
      "PostgreSQL",
      "MongoDB",
      "Redis",
      "OpenAI",
      "Anthropic",
      "GitHub Actions",
    ],
    deliverables: [
      "Modernized backend services for multiple legacy applications",
      "Web + mobile applications shipped end-to-end",
      "Production cloud infrastructure with IaC + DevOps automation",
      "Embedded contract engineering teams across active programs",
    ],
  },

  {
    slug: "fenmo",
    client: "fenmo",
    accent: "green",
    industry: "Financial Auditing · Reconciliation Automation",
    url: "https://fenmo.io",
    duration: "Foundational build engagement",
    services: [
      "Application Development",
      "DevOps",
      "Data Engineering",
      "Cloud",
    ],
    summary:
      "fenmo is a financial auditing platform that automates reconciliations for finance teams. United Techlab partnered with them to build the core systems, ingestion pipelines, automations, and the DevOps & cloud foundation underneath.",
    headline:
      "We built the engineering backbone of an automated reconciliation platform: pipelines, systems, and DevOps foundation.",
    pullQuote:
      "They thought about correctness the way a finance auditor thinks about correctness. Every pipeline came with verification baked in. That's exactly what an audit-grade product needs.",
    pullQuoteRole: "Engineering Leadership, fenmo",
    problem: [
      "Reconciliation today is still mostly Excel-and-email: slow, error-prone, and impossible to audit at scale.",
      "fenmo needed a platform that could ingest heterogenous financial data, normalize it, run rule + ML-based matching, and surface exceptions reliably enough that audit teams could trust it.",
      "All of this had to be production-grade from day one, no prototypes-in-prod, no silent drift.",
    ],
    approach: [
      "Designed and built the core reconciliation engine, a deterministic matcher with a configurable rules layer and an exception workflow on top.",
      "Stood up data ingestion pipelines for ledger feeds, bank statements, and operational systems, with idempotent retries and lineage tracking.",
      "Wired automations end-to-end: scheduled jobs, alerting, and reviewer workflows, so finance teams interact with results, not infrastructure.",
      "Set up the DevOps + cloud foundation (CI/CD, IaC, monitoring, secrets) so every change is reviewable, reversible, and traceable.",
    ],
    outcomes: [
      { label: "Manual reconciliation effort reduced", value: "~80%" },
      { label: "Match accuracy on production data", value: "99%+" },
      { label: "Auto-resolved transactions per cycle", value: "Tens of thousands" },
      { label: "Time-to-close reduced", value: "Days → Hours" },
    ],
    techUsed: [
      "Python",
      "FastAPI",
      "PostgreSQL",
      "Redis",
      "Apache Kafka",
      "Airflow",
      "AWS",
      "Docker",
      "Kubernetes",
      "Terraform",
      "React",
      "TypeScript",
      "GitHub Actions",
    ],
    deliverables: [
      "Reconciliation engine with rules + matching logic",
      "Ingestion pipelines for ledger / bank / ops data",
      "Operational automations + alerting",
      "Cloud + DevOps foundation",
    ],
  },

  {
    slug: "kainest",
    client: "KAINest",
    accent: "cyan",
    industry: "Enterprise IT Services & Products",
    url: "https://kainest.com",
    duration: "Ongoing contract-engineering engagement",
    services: [
      "Contract Engineers",
      "Application Development",
      "DevOps",
      "Cloud",
    ],
    summary:
      "KAINest is an enterprise IT services and products company. Their flagship product is LexiumBRF. United Techlab provides contract engineers who work directly inside KAINest's product team, building application features, automations, DevOps pipelines, and cloud infrastructure for LexiumBRF.",
    headline:
      "Embedded senior engineers powering LexiumBRF, KAINest's flagship enterprise product.",
    pullQuote:
      "United Techlab's engineers don't behave like a vendor. They behave like senior product engineers who happen to be on contract. Our LexiumBRF velocity reflects that directly.",
    pullQuoteRole: "Product Leadership, KAINest",
    problem: [
      "Building a flagship enterprise product (LexiumBRF) at the pace the market demanded, while keeping engineering quality high enough for enterprise procurement.",
      "Hiring full-time senior engineers in the relevant niche was slow, but staff augmentation with junior contractors would have eroded product quality.",
      "Product needed cohesive ownership across application, automations, DevOps, and cloud, not siloed handoffs.",
    ],
    approach: [
      "Placed senior contract engineers from United Techlab directly inside KAINest's LexiumBRF product team, not a vendor pod, but embedded contributors.",
      "Owned application development workstreams end-to-end alongside KAINest engineers, covering features, code review, and on-call rotations.",
      "Built and maintained DevOps automation (CI/CD, environments, release tooling) and cloud infrastructure to support LexiumBRF's enterprise deployment model.",
      "Kept ownership transparent, with every contractor reporting into KAINest's leadership, not a layered vendor org.",
    ],
    outcomes: [
      { label: "Senior engineers embedded", value: "Multi-engineer pod" },
      { label: "Time-to-onboard new engineer", value: "< 1 week" },
      { label: "Release cadence on LexiumBRF", value: "Sustained weekly" },
      { label: "Quality bar maintained", value: "Enterprise-grade" },
    ],
    techUsed: [
      "Java",
      "Python",
      "Node.js",
      "React",
      "TypeScript",
      "PostgreSQL",
      "MongoDB",
      "Docker",
      "Kubernetes",
      "AWS",
      "Azure",
      "Terraform",
      "GitHub",
      "GitLab",
    ],
    deliverables: [
      "Embedded contract engineering pod for LexiumBRF",
      "Application development workstreams owned end-to-end",
      "DevOps automation + release tooling",
      "Cloud infrastructure for enterprise deployments",
    ],
  },

  {
    slug: "adrush",
    client: "Adrush",
    accent: "green",
    industry: "AI Dictionary",
    url: "#",
    duration: "Short, focused build",
    services: [
      "Application Development",
      "Automation",
      "Cloud",
    ],
    summary:
      "Adrush is an AI-powered dictionary product. United Techlab took the project from concept to live website in a tight, focused engagement, building the application, supporting automations, and the deployment around it.",
    headline:
      "From idea to a live AI dictionary product, shipped in a tight, focused build.",
    pullQuote:
      "Short brief, fast turnaround, no drama. They treated a small project with the same engineering seriousness as a large one. That's why we'll come back for the next one.",
    pullQuoteRole: "Founder, Adrush",
    problem: [
      "A clear product vision (an AI-powered dictionary) but no engineering team in place to build it.",
      "Needed a partner that could go from blank canvas to a working, deployed product without overengineering it.",
      "Tight timeline; the brief was small, but every part of it had to actually work in production.",
    ],
    approach: [
      "Scoped the MVP in a single working session, locking the smallest version of the product that would deliver the experience the founder wanted.",
      "Built the application end-to-end: the user-facing website, the AI lookup flow, and the supporting backend.",
      "Wired the automations needed to keep the product running without manual operator intervention.",
      "Deployed onto a managed cloud setup so the founder could iterate without owning DevOps from day one.",
    ],
    outcomes: [
      { label: "Time to live website", value: "Weeks, not months" },
      { label: "Scope creep in delivery", value: "Zero" },
      { label: "Handover quality", value: "Founder-operable from day 1" },
      { label: "Live production product", value: "Shipped" },
    ],
    techUsed: [
      "React",
      "TypeScript",
      "Node.js",
      "Python",
      "OpenAI",
      "PostgreSQL",
      "Vercel",
      "Cloudflare",
    ],
    deliverables: [
      "Live AI dictionary website",
      "AI lookup flow + supporting backend",
      "Operational automations",
      "Cloud deployment + handover",
    ],
  },
];

export function getCaseStudyBySlug(slug) {
  return CASE_STUDIES.find((c) => c.slug === slug);
}
